import { useAppStore } from '@/store/store';
import {
  selectAirVsOceanComparison,
  selectCargoDensity,
  selectBreakEvenDensity,
  selectFCLCost,
} from '@/store/selectors';
import { Plane, Ship, Container } from 'lucide-react';

export function ModeComparison() {
  const costInput = useAppStore((s) => s.costInput);
  const comparison = useAppStore(selectAirVsOceanComparison);
  const density = useAppStore(selectCargoDensity);
  const breakEven = useAppStore(selectBreakEvenDensity);
  const fclCost = useAppStore(selectFCLCost);

  const hasRates = costInput.ratePerKg > 0 && costInput.ratePerCBM > 0;
  if (!hasRates) {
    return (
      <div className="card" data-testid="mode-comparison">
        <h3 className="section-title">Air vs Ocean Comparison</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Enter both air rate (per kg) and ocean rate (per CBM) in the Cost Panel to see the comparison.
        </p>
      </div>
    );
  }

  const airTotal = comparison.airCost.totalCost;
  const oceanTotal = comparison.oceanCost.totalCost;
  const fclTotal = fclCost?.totalCost ?? Infinity;
  const cheapestTotal = Math.min(airTotal, oceanTotal, fclTotal);
  const cheapest = fclTotal === cheapestTotal && fclCost
    ? 'fcl'
    : airTotal <= oceanTotal ? 'air' : 'ocean';

  const densityNote =
    density > 0 && density < breakEven
      ? 'Cargo is volume-driven (below break-even) — ocean LCL is typically more cost-efficient.'
      : 'Cargo is dense (above break-even) — air\'s per-kg premium is lower.';

  const cardStyle = (isWinner: boolean): React.CSSProperties => ({
    flex: 1,
    padding: 'var(--space-3)',
    background: isWinner ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-md)',
    border: isWinner ? '2px solid var(--color-success)' : '1px solid var(--color-border)',
  });

  return (
    <div className="card" data-testid="mode-comparison">
      <h3 className="section-title">
        Mode Comparison{fclCost ? ' (Air / Ocean / FCL)' : ' (Air vs Ocean)'}
      </h3>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={cardStyle(cheapest === 'air')}>
          <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>
            <Plane size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> Air Freight
            {cheapest === 'air' && <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>CHEAPER</span>}
          </div>
          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', fontFamily: 'var(--font-family-mono)' }}>
            {comparison.airCost.currency} {comparison.airCost.totalCost.toFixed(2)}
          </div>
        </div>
        <div style={cardStyle(cheapest === 'ocean')}>
          <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>
            <Ship size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> Ocean LCL
            {cheapest === 'ocean' && <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>CHEAPER</span>}
          </div>
          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', fontFamily: 'var(--font-family-mono)' }}>
            {comparison.oceanCost.currency} {comparison.oceanCost.totalCost.toFixed(2)}
          </div>
        </div>
        {fclCost && (
          <div style={cardStyle(cheapest === 'fcl')}>
            <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>
              <Container size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> FCL
              {cheapest === 'fcl' && <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>CHEAPER</span>}
            </div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', fontFamily: 'var(--font-family-mono)' }}>
              {fclCost.currency} {fclCost.totalCost.toFixed(2)}
            </div>
          </div>
        )}
      </div>
      {fclCost && fclTotal < oceanTotal && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
          FCL beats LCL by {fclCost.currency} {(oceanTotal - fclTotal).toFixed(2)}
        </div>
      )}
      {density > 0 && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
          {densityNote}
        </div>
      )}
      {!fclCost && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
          Savings: {comparison.airCost.currency} {comparison.savings.toFixed(2)} by choosing {cheapest === 'air' ? 'air' : 'ocean'}
        </div>
      )}
    </div>
  );
}
